import { getAccessToken } from '@connect-x-app/hybrid-utils';
import { ChatFetcherInput, ChatFetcherOptions, fetchServerSentEvents } from '@tanstack/ai-react';

import { ensureValidAccessToken } from '../utils';

type AgentAccess = { expiresAt: string; token: string; url: string };

export type TurnaroundAgent = { agentName: string; serviceUrl: string };

// fetch() instead of HttpClient (AI.md exception): the agent-service and the agent are separate hosts called with a
// bearer token, and TanStack AI's fetchServerSentEvents needs a fetch to stream the answer. The agent-service exchanges
// the Connect token for an agent token and the agent's URL.
const getAgentAccess = async ({ agentName, serviceUrl }: TurnaroundAgent, signal: AbortSignal): Promise<AgentAccess> => {
  await ensureValidAccessToken();
  const response = await fetch(`${serviceUrl}/agents/${encodeURIComponent(agentName)}/access`, {
    credentials: 'omit',
    headers: { Authorization: `Bearer ${await getAccessToken()}` },
    method: 'POST',
    signal,
  });
  if (!response.ok) {
    throw new Error(`Agent access failed: HTTP ${response.status}`);
  }
  return response.json() as Promise<AgentAccess>;
};

export const fetchTurnaroundAgent = async (
  agent: TurnaroundAgent,
  { data, messages, ...runContext }: ChatFetcherInput,
  { headers, signal }: ChatFetcherOptions,
) => {
  const { token, url } = await getAgentAccess(agent, signal);
  return fetchServerSentEvents(url, {
    headers: {
      Accept: 'text/event-stream',
      Authorization: `Bearer ${token}`,
      'X-Amzn-Bedrock-AgentCore-Runtime-Session-Id': runContext.threadId,
    },
  }).connect(messages, data, signal, { ...runContext, headers });
};
